import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useSearchParams } from 'react-router-dom';
import { 
  FlaskConical, Upload, FileText, Trash2, ChevronRight, 
  LogOut, Loader2, Search, Clock, Sparkles, Crown, Menu, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { paperApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import AnalysisView from '../components/AnalysisView';
import PricingModal from '../components/PricingModal';
import UsageBanner from '../components/UsageBanner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [papers, setPapers] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPricing, setShowPricing] = useState(false);
  const [usage, setUsage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchPapers = useCallback(async () => {
    try {
      const response = await paperApi.getAll();
      setPapers(response.data);
    } catch {
      toast.error('Failed to fetch papers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const token = localStorage.getItem('simplify_token');
      const response = await axios.get(`${API_URL}/api/subscription/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId, planId) => {
    try {
      const token = localStorage.getItem('simplify_token');
      const response = await axios.get(
        `${API_URL}/api/subscription/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'paid') {
        toast.success(`Successfully upgraded to ${planId} plan!`);
        fetchUsage();
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    } finally {
      setSearchParams({});
    }
  }, [fetchUsage, setSearchParams]);

  useEffect(() => {
    fetchPapers();
    fetchUsage();
    
    const sessionId = searchParams.get('session_id');
    const planId = searchParams.get('plan');
    if (sessionId) {
      checkPaymentStatus(sessionId, planId);
    }
  }, [fetchPapers, fetchUsage, searchParams, checkPaymentStatus]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploading(true);
    // Create a placeholder so spinner shows immediately
    const placeholderPaper = { id: 'uploading', title: file.name, analyzed: false };
    setSelectedPaper(placeholderPaper);
    
    try {
      const response = await paperApi.upload(file);
      const uploadedPaper = response.data;
      setPapers(prev => [uploadedPaper, ...prev]);
      setSelectedPaper(uploadedPaper);
      toast.success('Paper uploaded! Starting analysis...');
      setSidebarOpen(false);
      
      // Auto-start analysis
      try {
const analysisResponse = await paperApi.analyze(uploadedPaper.id);
  const analyzedPaper = {
    ...uploadedPaper,
    analyzed: true,
    analysis: analysisResponse.data.analysis
};
setPapers(prev => prev.map(p => p.id === analyzedPaper.id ? analyzedPaper : p));

// Force fresh fetch for mobile reliability
try {
  const token = localStorage.getItem('simplify_token');
  const freshResponse = await axios.get(`${API_URL}/api/papers/${uploadedPaper.id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  setSelectedPaper(freshResponse.data);
} catch {
  setSelectedPaper(analyzedPaper);
}
        fetchUsage();
        
        if (analysisResponse.data.analysis?.analysis_issues) {
          toast.warning('Analysis complete with some limitations');
        } else {
          toast.success('Analysis complete!');
        }
      } catch (analysisError) {
        if (analysisError.response?.status === 403) {
          setShowPricing(true);
          toast.error('Usage limit reached. Please upgrade to continue.');
        } else {
          toast.error('Analysis failed. You can retry from the paper view.');
        }
      }
    } catch {
      toast.error('Failed to upload paper');
    } finally {
      setUploading(false);
    }
  }, [fetchUsage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false
  });

  const handleDelete = async (paperId, e) => {
    e.stopPropagation();
    try {
      await paperApi.delete(paperId);
      setPapers(prev => prev.filter(p => p.id !== paperId));
      if (selectedPaper?.id === paperId) {
        setSelectedPaper(null);
      }
      toast.success('Paper deleted');
    } catch {
      toast.error('Failed to delete paper');
    }
  };

const handleSelectPaper = async (paper) => {
    setSidebarOpen(false);
    
    // Always fetch fresh paper data from server
    try {
      const token = localStorage.getItem('simplify_token');
      const response = await axios.get(`${API_URL}/api/papers/${paper.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const freshPaper = response.data;
      setSelectedPaper(freshPaper);
      setPapers(prev => prev.map(p => p.id === freshPaper.id ? freshPaper : p));
      paper = freshPaper;
    } catch {
      setSelectedPaper(paper);
    }
    
    // Auto-analyze if not already analyzed
    if (!paper.analyzed) {
      setUploading(true); // Use uploading state to show loading UI
      try {
        const analysisResponse = await paperApi.analyze(paper.id);
        const analyzedPaper = {
          ...paper,
          analyzed: true,
          analysis: analysisResponse.data.analysis
        };
        setPapers(prev => prev.map(p => p.id === analyzedPaper.id ? analyzedPaper : p));
        setSelectedPaper(analyzedPaper);
        fetchUsage();
        
        if (analysisResponse.data.analysis?.analysis_issues) {
          toast.warning('Analysis complete with some limitations');
        } else {
          toast.success('Analysis complete!');
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setShowPricing(true);
          toast.error('Usage limit reached. Please upgrade to continue.');
        } else {
          toast.error('Analysis failed. Please try again.');
        }
      } finally {
        setUploading(false);
      }
    }
  };

  const handleAnalysisComplete = (updatedPaper) => {
    setPapers(prev => prev.map(p => p.id === updatedPaper.id ? updatedPaper : p));
    setSelectedPaper(updatedPaper);
    fetchUsage();
  };

  const handleAnalysisError = (error) => {
    if (error.response?.status === 403) {
      setShowPricing(true);
    }
  };

  const filteredPapers = papers.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col lg:flex-row" data-testid="dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700/50 bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-teal-400/20 border border-teal-400/30">
            <FlaskConical className="w-5 h-5 text-teal-400" />
          </div>
          <a href="/" className="text-lg font-heading font-bold text-white" style={{textDecoration: 'none'}}>Simplify</a>
        </div>
        <div className="flex items-center gap-2">
          {usage && (
            <button
              onClick={() => setShowPricing(true)}
              className="p-2 rounded-lg bg-teal-500/10 text-teal-400"
              data-testid="mobile-upgrade-button"
            >
              <Crown className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg bg-slate-700/50 text-white"
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Sidebar - Desktop always visible, Mobile as overlay */}
      <aside className={`
        ${sidebarOpen ? 'fixed inset-0 z-50 lg:relative lg:inset-auto' : 'hidden lg:flex'}
        w-full lg:w-80 bg-slate-800/95 lg:bg-slate-800/50 border-r border-slate-700/50 flex-col
        lg:flex
      `}>
        {/* Mobile Close Button */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700/50">
          <span className="text-lg font-heading font-semibold text-white">Papers</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Logo - Desktop only */}
        <div className="hidden lg:flex p-6 border-b border-slate-700/50">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-teal-400/20 border border-teal-400/30">
                <FlaskConical className="w-6 h-6 text-teal-400" />
              </div>
              <a href="/" className="text-xl font-heading font-bold text-white" style={{textDecoration: 'none'}}>Simplify</a>
            </div>
            {usage && (
              <button
                onClick={() => setShowPricing(true)}
                className="p-2 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 transition-colors"
                data-testid="upgrade-button"
                title="Upgrade Plan"
              >
                <Crown className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search papers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-papers-input"
              className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-400 rounded-xl"
            />
          </div>
        </div>

        {/* Papers List */}
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-2 pb-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
              </div>
            ) : filteredPapers.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No papers yet</p>
                <p className="text-xs mt-1">Upload your first research paper</p>
              </div>
            ) : (
              filteredPapers.map((paper) => (
                <motion.div
                  key={paper.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleSelectPaper(paper)}
                  data-testid={`paper-item-${paper.id}`}
                  className={`
                    p-3 lg:p-4 rounded-xl cursor-pointer transition-all duration-200 group
                    ${selectedPaper?.id === paper.id 
                      ? 'bg-teal-400/10 border border-teal-400/30' 
                      : 'bg-slate-800/30 border border-transparent hover:bg-slate-800/50 hover:border-slate-700'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate pr-2">
                        {paper.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-xs text-slate-500">{formatDate(paper.created_at)}</span>
                        {paper.analyzed && (
                          <span className="px-2 py-0.5 text-xs bg-teal-400/20 text-teal-400 rounded-full">
                            Analyzed
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDelete(paper.id, e)}
                      data-testid={`delete-paper-${paper.id}`}
                      className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-400/10 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-400/20 flex items-center justify-center">
                <span className="text-teal-400 font-medium">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white truncate max-w-[120px]">
                  {user?.name}
                </p>
                <p className="text-xs text-slate-400 truncate max-w-[120px]">
                  {usage?.plan_name || 'Free'} Plan
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Usage Banner */}
        <UsageBanner usage={usage} onUpgrade={() => setShowPricing(true)} />

        {/* Upload Zone - Hidden while uploading */}
        {!uploading && (
          <div className="p-4 lg:p-6 border-b border-slate-700/50">
            <div
              {...getRootProps()}
              data-testid="pdf-dropzone"
              className={`
                relative overflow-hidden rounded-xl lg:rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer
                ${isDragActive 
                  ? 'border-teal-400 bg-teal-400/5' 
                  : 'border-slate-600 hover:border-teal-400/50 hover:bg-slate-800/30'
                }
              `}
            >
              <input {...getInputProps()} />
              <div className="flex items-center justify-center gap-4 lg:gap-6 p-4 lg:p-8">
                <div className={`
                  p-3 lg:p-4 rounded-xl lg:rounded-2xl transition-all duration-300
                  ${isDragActive ? 'bg-teal-400/20 scale-110' : 'bg-slate-800'}
                `}>
                  <Upload className={`w-6 h-6 lg:w-8 lg:h-8 ${isDragActive ? 'text-teal-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h3 className="text-base lg:text-lg font-heading font-semibold text-white mb-1">
                    {isDragActive ? 'Drop your PDF here' : 'Upload Research Paper'}
                  </h3>
                  <p className="text-xs lg:text-sm text-slate-400">
                    Drag & drop a PDF or tap to browse
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis View or Welcome */}
        <div className="flex-1 overflow-auto">
          <AnimatePresence mode="wait">
            {selectedPaper ? (
              <AnalysisView 
                key={selectedPaper.id}
                paper={selectedPaper} 
                onAnalysisComplete={handleAnalysisComplete}
                onAnalysisError={handleAnalysisError}
                isUploading={uploading}
              />
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-start justify-center p-4 pt-12"
              >
                <div className="text-center max-w-md px-4 lg:px-6">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-teal-400/20 to-emerald-400/20 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 lg:w-10 lg:h-10 text-teal-400" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-heading font-bold text-white mb-2 lg:mb-3">
                    Ready to Analyze
                  </h2>
                  <p className="text-sm lg:text-base text-slate-400 leading-relaxed">
                    Upload any research paper to get AI-powered insights, 
                    simplified explanations, and actionable summaries.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        currentPlan={usage?.plan || 'free'}
        usage={usage}
        onUpgradeSuccess={() => {
          setShowPricing(false);
          fetchUsage();
        }}
      />
    </div>
  );
};

export default Dashboard;
